import { useState } from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet } from "lucide-react";
import { grnApi } from "../../api";
import { ProcError, ProcLoading, ProcPagination } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useAsyncData, usePageState } from "../../hooks";

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

export default function GRNListPage() {
  const { page, size, setPage, state } = usePageState();
  const [exporting, setExporting] = useState(false);

  const { data, loading, error } = useAsyncData(
    () => grnApi.list({ page: state.page, size: state.size }),
    [state.page, state.size],
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Goods Received Notes</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => { setExporting(true); try { downloadBlob(await grnApi.export(), `grn-register-${new Date().toISOString().slice(0,10)}.xlsx`); } finally { setExporting(false); } }}
            disabled={exporting}
            className="flex items-center gap-1.5 rounded-xl border border-emerald-300 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
          >
            <FileSpreadsheet className="h-4 w-4" />
            {exporting ? "Exporting…" : "Export Excel"}
          </button>
          <Link to="/procurement/grns/new" className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
            New GRN
          </Link>
        </div>
      </div>

      {loading && <ProcLoading />}
      {error && <ProcError message={error} />}

      {!loading && !error && data && (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Serial No</th>
                  <th className="px-4 py-3">LPO Serial</th>
                  <th className="px-4 py-3">Received From</th>
                  <th className="px-4 py-3">Delivery Note</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Form Date</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.content.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No GRNs found.</td></tr>
                )}
                {data.content.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900">{r.serialNo}</td>
                    <td className="px-4 py-3 text-slate-600">{r.lpoSerialNo || String(r.lpoId)}</td>
                    <td className="px-4 py-3 text-slate-700">{r.receivedFrom}</td>
                    <td className="px-4 py-3 text-slate-600">{r.deliveryNoteNo}</td>
                    <td className="px-4 py-3"><ProcStatusBadge value={r.status} /></td>
                    <td className="px-4 py-3 text-slate-500">{r.date ? new Date(r.date).toLocaleDateString("en-KE") : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/procurement/grns/${r.id}`} className="font-semibold text-emerald-700 hover:underline">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ProcPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}
